import { useState } from 'react';
import api from '../services/api';

const fmt = n => Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Import() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setPreview(null);
    setResult(null);
    setError('');
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post('/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(r.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
    setLoading(false);
  };

  const handleImport = async () => {
    if (!preview) return;
    const validEntries = preview.preview.filter(e => e.valid);
    if (validEntries.length === 0) {
      setError('No valid entries to import');
      return;
    }
    setImporting(true);
    setError('');
    try {
      const r = await api.post('/import/execute', { entries: validEntries });
      setResult(r.data);
      setPreview(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
    setImporting(false);
  };

  const downloadTemplate = () => {
    api.get('/import/template', { responseType: 'blob' }).then(r => {
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bill-import-template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    });
  };

  return (
    <div>
      <h1 className="page-title">Import Bills from Excel</h1>

      <div className="card" style={{ maxWidth: 800, marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 8px' }}>Instructions</h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '0 0 12px' }}>
          Import outstanding bills, balances, and penalties from your old Excel system. The Excel file must have an <strong>account_code</strong> column that matches existing consumers.
        </p>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
          <strong>Required column:</strong> account_code<br />
          <strong>Optional columns:</strong> billing_period, water_charge, past_due, penalty, total_amount, payment_amount, status
        </div>
        <button className="btn btn-sm" style={{ background: 'var(--bg-input)', color: 'var(--text-primary)' }} onClick={downloadTemplate}>
          Download Template (.xlsx)
        </button>
      </div>

      <div className="card" style={{ maxWidth: 800, marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px' }}>Upload File</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileChange}
            style={{ fontSize: 14 }} />
          <button className="btn btn-primary" onClick={handlePreview} disabled={!file || loading}>
            {loading ? 'Analyzing...' : 'Preview'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: 'var(--danger-bg, #fde)', color: 'var(--danger, #d32f2f)', padding: 12, borderRadius: 6, marginBottom: 16, maxWidth: 800 }}>
          {error}
        </div>
      )}

      {result && (
        <div className="card" style={{ maxWidth: 800, marginBottom: 16, background: 'var(--success-bg, #e8f5e9)' }}>
          <h3 style={{ margin: '0 0 8px', color: 'var(--success, #2e7d32)' }}>Import Complete</h3>
          <p style={{ margin: 0, fontSize: 14 }}>
            {result.message}
          </p>
        </div>
      )}

      {preview && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <div>
                <h3 style={{ margin: 0 }}>Preview ({preview.total} rows)</h3>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                  <span style={{ color: 'var(--success, #2e7d32)' }}>{preview.valid} valid</span>
                  {preview.invalid > 0 && <span style={{ color: 'var(--danger, #d32f2f)', marginLeft: 12 }}>{preview.invalid} invalid (will be skipped)</span>}
                </div>
              </div>
              <button className="btn btn-primary" onClick={handleImport} disabled={importing || preview.valid === 0}>
                {importing ? 'Importing...' : `Import ${preview.valid} Bills`}
              </button>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Account Code</th>
                    <th>Consumer</th>
                    <th>Period</th>
                    <th className="text-right">Water Charge</th>
                    <th className="text-right">Past Due</th>
                    <th className="text-right">Penalty</th>
                    <th className="text-right">Total</th>
                    <th className="text-right">Paid</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.map((e, i) => (
                    <tr key={i} style={{ opacity: e.valid ? 1 : 0.5, background: e.valid ? undefined : 'var(--danger-bg, #fde)' }}>
                      <td>{e.row}</td>
                      <td>{e.account_code}</td>
                      <td>{e.consumer_name || <span style={{ color: 'var(--danger, #d32f2f)', fontSize: 12 }}>{e.error}</span>}</td>
                      <td>{e.billing_period}</td>
                      <td className="text-right">{fmt(e.water_charge)}</td>
                      <td className="text-right">{fmt(e.past_due)}</td>
                      <td className="text-right">{fmt(e.penalty)}</td>
                      <td className="text-right" style={{ fontWeight: 600 }}>{fmt(e.total_amount)}</td>
                      <td className="text-right">{fmt(e.payment_amount)}</td>
                      <td><span className={`badge ${e.status === 'Paid' ? 'badge-green' : e.status === 'Partial' ? 'badge-blue' : 'badge-orange'}`}>{e.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
